import {
    Avatar,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem
} from '@material-ui/core';
import { Check, Delete, MoreVert as MoreVertIcon, NotInterested } from '@material-ui/icons';
import * as React from 'react';

import { hasRole } from 'shared/models/user';

import { useLazyQuery, useMutation } from '@apollo/client';
import { deleteProfileLink, receiveProfileLinkUpdate } from '../actions';
import { GET_PERSON_PROFILE_LINKS, MARK_INVALID_LINK } from '../graphql/queries/profiles';
import { useReduxDispatch, useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { State } from '../state';

const avatarSize = 18;
const iconSizeRatio = 0.75;
const colorRed400 = '#ef5350';
const colorRed600 = '#e53935';

interface ProfileUrlAttributesProps {
    url: string;
    personId: string;
    invalid: boolean;
}

export const ProfileUrlAttributes: React.FC<ProfileUrlAttributesProps> = ({ url, personId, invalid }) => {
    const pendingRequests = useReduxState((state: State) => state.pendingRequests);
    const { userPermissions } = useSession();
    const updateRequestKey = `website-update-request-${url}`;
    const disabled = pendingRequests.has(updateRequestKey) && pendingRequests.get(updateRequestKey).isEmpty();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [updateLink] = useMutation<{}, { url: string; invalid: boolean }>(MARK_INVALID_LINK);
    const [getLinks] = useLazyQuery<
        {
            person: {
                personId: string;
                profileUrls: Array<{
                    id: string;
                    personId: string;
                    url: string;
                    createdAt: number;
                    modifiedAt: number;
                    invalid: boolean;
                }>;
            };
        },
        { personId: string }
    >(GET_PERSON_PROFILE_LINKS, { variables: { personId }, fetchPolicy: 'network-only' });
    const dispatch = useReduxDispatch();

    const handleInvalid = async () => {
        if (!disabled) {
            await updateLink({ variables: { url, invalid: !invalid } });
            const result = await getLinks();
            dispatch(receiveProfileLinkUpdate({ ...result.data.person, url }));
            setAnchorEl(null);
        }
    };

    const handleLinkDelete = () => {
        if (!disabled) {
            dispatch(deleteProfileLink(personId, url));
            setAnchorEl(null);
        }
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleCloseMenu = () => setAnchorEl(null);

    let content: JSX.Element;
    if (disabled) {
        const spinnerSize = 20;
        const spinnerThickness = 2;
        content = <CircularProgress size={spinnerSize} thickness={spinnerThickness} />;
    } else {
        const invalidIcon = (
            <Avatar
                style={{ width: avatarSize, height: avatarSize, backgroundColor: colorRed400 }}
                className="avatar-contact-attribute"
            >
                <NotInterested style={{ color: '#fff', fontSize: avatarSize * iconSizeRatio }} />
            </Avatar>
        );

        const removeIcon = (
            <Avatar
                style={{ width: avatarSize, height: avatarSize, backgroundColor: colorRed600 }}
                className="avatar-contact-attribute"
            >
                <Delete style={{ color: '#fff', fontSize: avatarSize * iconSizeRatio }} />
            </Avatar>
        );

        const button = (
            <IconButton disableRipple={true} style={{ width: 16, height: 16, padding: 0 }} onClick={handleOpenMenu}>
                <MoreVertIcon className="icon-button-more" style={{ height: 16, width: 16 }} />
            </IconButton>
        );

        const invalidTrailingIcon = invalid ? (
            <ListItemIcon style={{ minWidth: 0, marginLeft: 'auto' }}>
                <Check />
            </ListItemIcon>
        ) : null;
        const invalidMenuItem = (
            <MenuItem key="invalid" onClick={handleInvalid}>
                <ListItemIcon style={{ minWidth: 32 }}>{invalidIcon}</ListItemIcon>
                <ListItemText primary="Mark as invalid" />
                {invalidTrailingIcon}
            </MenuItem>
        );
        const menuItems: JSX.Element[] = [invalidMenuItem];

        if (hasRole(userPermissions, 'profile_links_editor')) {
            menuItems.push(<Divider key="divider" component="li" />);
            const removeMenuItem = (
                <MenuItem key="remove" onClick={handleLinkDelete}>
                    <ListItemIcon style={{ minWidth: 32 }}>{removeIcon}</ListItemIcon>
                    <ListItemText primary="Delete" />
                </MenuItem>
            );
            menuItems.push(removeMenuItem);
        }

        content = (
            <div className="contact-attributes">
                <div className="list-item-menu-button">
                    {button}
                    <Menu
                        anchorEl={anchorEl}
                        keepMounted={true}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    >
                        {menuItems}
                    </Menu>
                </div>
                {invalid ? invalidIcon : null}
            </div>
        );
    }

    return content;
};
